import DocVerifiedStatus from '@/enums/docVerifiedStatus'
import TaskStatus from '@/enums/taskStatus'
import TaskType from '@/enums/taskType'
import {
  commonWorkflowService,
  ecrPrerequisitesService,
  oorPrerequisitesService,
  orgResearchService,
  orgService,
  searchService,
  utilWorkflowService,
  vleiPrerequisitesService,
  workflowInstanceService
} from '@/utils/apis'
import {
  CompleteInstance1Request,
  CompleteInstanceRequest,
  DarProposalRequest,
  type DarProposalRequestDto,
  DarReplacementRequest,
  FindAvailableQarsRequest,
  type IdVerificationRequestDto,
  type LarProposalRequestDto,
  type LarRemovalRequestDto,
  type OrgFillingDetailRequestDto,
  type QarAcceptsDarRequestDto,
  type QarRejectsDarRequestDto,
  type VLEIPrerequisitesRequestDto
} from '@/api/origin-workflow-svc'
export interface ITaskActor {
  workflowInstanceAssignedTaskId: string
  userId: string
  userName: string
  workflowInstanceId: string
  taskType: TaskType
  symbolicTextId: string | null
  status: TaskStatus | DocVerifiedStatus
  callbackUrl: string
}
export interface ITaskType {
  workflowModelId: string
  taskType: TaskType
  symbolicTextId: string
  taskActors: ITaskActor[]
}

export interface IProposedUserResponse {
  userId: string
  userName: string
  orgId: string
  orgName?: string
  emails: string[]
  phoneNumbers?: {
    userId: string
    value: string
    countryCode: string
  }[]
  grant: string
  isAccepted: boolean
  approvedByUser: string
  proposedByUser: string
}

interface Attendee {
  userId: string
  name: string
}

interface CeremonyTime {
  start: string
  end: string
}

export interface CeremonyResponse {
  attendees: Attendee[]
  ceremonyTime: CeremonyTime
  title: string
  meetingUrl: string
}

export interface MeetingWorkflowData {
  workflowInstanceId: string
  orgId: string
  lei: string
}

export async function getWorkflows(workflowStatus?: string) {
  return await workflowInstanceService.findAllForRequester({
    withTasks: true,
    workflowStatus
  })
}

export async function startWorkflow(
  vLEIPrerequisitesRequestDto: VLEIPrerequisitesRequestDto
) {
  return await vleiPrerequisitesService.start1({
    vLEIPrerequisitesRequestDto
  })
}

export async function getOrganizationDetails(orgId: string) {
  return await orgService.getOrg({ orgId })
}

export async function getOrganizationResearchDetails(orgResearchId: string) {
  return await orgResearchService.findById1({ orgResearchId })
}

export async function searchOrganizationByLEI(lei: string) {
  return searchService.searchByLei({ lei })
}

//returned org_research_id
export async function boundOrgResearchWithOrgId(
  orgResearch: string,
  org: string
) {
  return await orgResearchService.boundOrgResearch({ org, orgResearch })
}

export async function updateOrgDetails(
  orgFillingDetailRequestDto: OrgFillingDetailRequestDto
) {
  return await vleiPrerequisitesService.orgFillingDetail({
    orgFillingDetailRequestDto
  })
}

export async function proposeDARCoordinator(
  darProposalRequestDto: DarProposalRequestDto
) {
  return await vleiPrerequisitesService.darProposal({
    darProposalRequestDto
  })
}
export async function proposeLARRepresentative(
  larProposalRequestDto: LarProposalRequestDto
) {
  return vleiPrerequisitesService.larProposal({
    larProposalRequestDto
  })
}

// todo ask BE to add enum instead of string for DAR
export async function getProposedDARDetailsByOrgID(org: string) {
  return utilWorkflowService.findAllUsersInOrgAndGrant({
    org,
    role: 'DAR'
  })
}

export async function getProposedLARDetailsByOrgID(org: string) {
  return utilWorkflowService.findAllUsersInOrgAndGrant({
    org,
    role: 'lar'
  })
}

export async function startIdentityVerification(
  idVerificationRequestDto: IdVerificationRequestDto
) {
  const mocked = !!JSON.parse(
    localStorage.getItem('mockIdVerification') ?? 'null'
  )
  return commonWorkflowService.idVerification({
    idVerificationRequestDto,
    mocked
  })
}

export async function getCeremonyTime(workflow: string) {
  return await commonWorkflowService.findScheduledMeeting({
    workflow
  })
}

export async function getAvailableQARs(payload: FindAvailableQarsRequest) {
  return await commonWorkflowService.findAvailableQars(payload)
}

export async function getWorkflowIdByMeetingId(meeting: string) {
  return await commonWorkflowService.findMeetingOrgParameters({ meeting })
}

export async function approveProposedCoordinator(
  qarAcceptsDarRequestDto: QarAcceptsDarRequestDto
) {
  return await vleiPrerequisitesService.qarAcceptsDar({
    qarAcceptsDarRequestDto
  })
}

export async function rejectProposedCoordinator(
  qarRejectsDarRequestDto: QarRejectsDarRequestDto
) {
  return vleiPrerequisitesService.qarRejectsDar({
    qarRejectsDarRequestDto
  })
}

export async function removeProposedLar(
  larRemovalRequestDto: LarRemovalRequestDto
) {
  return vleiPrerequisitesService.larRemoval({
    larRemovalRequestDto
  })
}

export async function completeWorkflow(payload: CompleteInstanceRequest) {
  return workflowInstanceService.completeInstance(payload)
}

export async function completeLEWorkflow(payload: CompleteInstance1Request) {
  return vleiPrerequisitesService.completeInstance1(payload)
}

export const sendCredentialRevocationNotification = async (
  aid: string,
  cred: string
) => {
  return await utilWorkflowService.credentialRevocationNotificationRaw({
    credentialRevocationNotificationDto: { aid, cred }
  })
}

export const getOrgDetailsByWorkflowId = async (workflowInstanceId: string) => {
  return await utilWorkflowService.orgDetail({
    workflow: workflowInstanceId
  })
}

export const QARApproveOrganization = async (workflowInstanceId: string) => {
  return await vleiPrerequisitesService.qarApprovesOrgParameters({
    qarAcceptsOrgRequestDto: { workflowInstanceId }
  })
}

export const qarVerifiesOrg = async (workflowInstanceId: string) => {
  return await oorPrerequisitesService.qarVerifiesOrg({
    qarAcceptsOrgRequestDto: { workflowInstanceId }
  })
}

export const DARReplaceMent = async (requestBody: DarReplacementRequest) => {
  return await vleiPrerequisitesService.darReplacement(requestBody)
}

export const getLarsFromWorkflowId = async (workflowId: string) => {
  return await utilWorkflowService.findOrgLars({ workflow: workflowId })
}

export const cancelLeCeremony = async (workflowInstanceId: string) => {
  return await vleiPrerequisitesService.cancelCeremony1({
    cancelCeremonyRequestDto: { workflowInstanceId }
  })
}

export const cancelOorCeremony = async (workflowInstanceId: string) => {
  return await oorPrerequisitesService.cancelCeremony({
    cancelCeremonyRequestDto: { workflowInstanceId }
  })
}

export const cancelEcrCeremony = async (workflowInstanceId: string) => {
  return await ecrPrerequisitesService.cancelCeremony2({
    cancelCeremonyRequestDto: { workflowInstanceId }
  })
}

export const getWorkflowByWorkflowInstanceId = (
  workflowInstanceId: string,
  withTasks: boolean = true
) => {
  return workflowInstanceService.findOne({ workflowInstanceId, withTasks })
}
